import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Analytics from 'analytics-opencare';

import { PrimaryButton } from 'brand-guidelines/Buttons-styled-components';
import { logoutUser } from 'connect/actions';
import { checkoutDataInitialState } from 'connect/checkoutInitialState';
import { selectDataUser } from 'connect/selectors';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { useNextCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';
import { useCheckoutRedesignAnalyticsData } from 'pages/checkout/utils/analyticsUtils';

import { AppleLogoSvg } from 'pages/common/components/svgs/AppleLogoSvg';
import DefaultProfileImageSvg from 'pages/common/components/svgs/DefaultProfileImageSvg';
import { FacebookLogoSvg } from 'pages/common/components/svgs/FacebookLogoSvg';
import { GoogleLogoSvg } from 'pages/common/components/svgs/GoogleLogoSvg';
import { SSO_PROVIDERS } from 'pages/legacy-checkout/helpers/checkout-constants';

import { Logout, PhotoWrapper, Text, UserName, Wrapper } from './LoggedIn-styled-components';

const analytics = new Analytics();

const ssoLogoProps = {
  size: '28px',
};

const getSSOLogo = (ssoMethod: SSO_PROVIDERS) => {
  const logos: { [key in SSO_PROVIDERS]: React.JSX.Element } = {
    [SSO_PROVIDERS.GOOGLE]: <GoogleLogoSvg {...ssoLogoProps} />,
    [SSO_PROVIDERS.FACEBOOK]: <FacebookLogoSvg {...ssoLogoProps} />,
    [SSO_PROVIDERS.APPLE]: <AppleLogoSvg {...ssoLogoProps} />,
  };

  return logos[ssoMethod];
};

const ProfilePhoto = ({ SsoLogo }: { SsoLogo: React.JSX.Element; }) => {
  if (!SsoLogo) {
    return <DefaultProfileImageSvg width={ssoLogoProps.size} height={ssoLogoProps.size} />;
  }

  return SsoLogo;
};

export const LoggedIn = ({ setSignUp }: { setSignUp: () => void; }) => {
  const dispatch = useDispatch();
  const analyticsData = useCheckoutRedesignAnalyticsData();

  const logOut = useCallback(() => {
    analytics.trackClick({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-info',
      name: 'checkout',
      section: 'logged-in',
      content: {
        action: 'logout',
        ...analyticsData.content,
      },
    });

    // Reset checkout data on logout
    dispatch(CheckoutStateHandler.update(checkoutDataInitialState));
    dispatch(logoutUser());
    setSignUp();
  }, [dispatch, setSignUp, analyticsData]);

  const { goToNextStep } = useNextCheckoutStep();

  const handleContinueBooking = () => {
    analytics.trackClick({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-info',
      name: 'checkout',
      section: 'logged-in',
      content: {
        action: 'continue-booking',
        ...analyticsData.content,
      },
    });

    goToNextStep();
  };

  const user = useSelector(selectDataUser);
  const ssoMethod = user.authStrategy;
  const SsoLogo = useMemo(() => getSSOLogo(ssoMethod), [ssoMethod]);

  return (
    <>
      <Wrapper>
        <PhotoWrapper>
          <ProfilePhoto SsoLogo={SsoLogo} />
        </PhotoWrapper>
        <Text>
          You're currently logged in as <UserName className="sensitive-data">{user.email}</UserName>
        </Text>
        <Logout onClick={logOut}>Log out, this isn't me</Logout>
      </Wrapper>
      <PrimaryButton onClick={handleContinueBooking}>Continue Booking</PrimaryButton>
    </>
  );
};
